import { Grid, Typography } from '@mui/material';
import {
  BooleanField,
  Datagrid,
  FunctionField,
  ListContextProvider,
  ShowButton,
  SimpleShowLayout,
  TextField,
  useList,
} from 'react-admin';
import { DataProviderResource } from 'src/constants';
import { AccountStatusChip, LinkAccountStatusChip, PhoneNumberArrayField } from 'src/lib';
import { BaseAccount, User } from 'src/models';
import { Spacing } from 'src/styles';

import { LinkAccountButton } from './LinkAccountButton';
import { MoveAccountButton } from './MoveAccountButton';
import { UnlinkAdditionalAccountButton } from './UnlinkAdditionalAccountButton';

interface UserAccountListProps {
  title?: string;
  user: User;
  rootAccountId?: string;
  accounts: BaseAccount[];
  canLink?: boolean;
  canUnlink?: boolean;
  canMove?: boolean;
  showIsPrimary?: boolean;
}

export const UserAccountList = ({
  title = 'Accounts',
  user,
  rootAccountId,
  accounts,
  canLink,
  canUnlink,
  canMove,
  showIsPrimary,
}: UserAccountListProps) => {
  const listContext = useList({
    data: accounts || [],
    isLoading: false,
    resource: DataProviderResource.Accounts,
  });

  return (
    <Grid container className={Spacing.my32}>
      <Grid item xs={12} md={12}>
        <SimpleShowLayout>
          <Typography variant='h5'>{title}</Typography>
        </SimpleShowLayout>
      </Grid>
      <Grid item xs={12} md={12}>
        <ListContextProvider
          value={{
            ...listContext,
          }}>
          <Datagrid
            resource={DataProviderResource.Accounts}
            bulkActionButtons={false}
            empty={
              <SimpleShowLayout>
                <Typography>No accounts found</Typography>
              </SimpleShowLayout>
            }>
            <TextField source='id' label='Id' />
            <TextField source='accountId' label='Account Id' />
            <TextField source='accountNumber' label='Account number' />
            <TextField source='customTypeLabel' label='Type' />
            <TextField source='primaryAccountHolder.firstName' label='First Name' />
            <TextField source='primaryAccountHolder.middleName' label='Middle Name' />
            <TextField source='primaryAccountHolder.lastName' label='Last Name' />
            <TextField source='primaryAccountHolder.suffix' label='Suffix' />
            <TextField source='primaryAccountHolder.email' label='Email' />
            <FunctionField
              render={(record: BaseAccount) => {
                return <PhoneNumberArrayField record={record.primaryAccountHolder?.phones} />;
              }}
              label='Phone Number'
            />
            <FunctionField
              label='Status'
              render={(account: BaseAccount) => {
                if (account.status) {
                  return <AccountStatusChip value={account.status} />;
                }

                return null;
              }}
            />
            {showIsPrimary && <BooleanField source='isRoot' label='Is Primary?' />}
            <FunctionField
              label='Link Status'
              render={(account: BaseAccount) => {
                if (account.id === rootAccountId) {
                  return 'NA';
                }

                return <LinkAccountStatusChip value={account.linkStatus} />;
              }}
            />
            {canLink && (
              <FunctionField
                label='Link'
                render={(account: BaseAccount) => {
                  if (account.id === rootAccountId) {
                    return 'NA';
                  }

                  return <LinkAccountButton account={account} rootAccountId={rootAccountId} />;
                }}
              />
            )}
            {canUnlink && (
              <FunctionField
                label='Unlink'
                render={(account: BaseAccount) => {
                  if (account.id === rootAccountId) {
                    return 'NA';
                  }

                  return <UnlinkAdditionalAccountButton account={account} rootAccountId={rootAccountId} />;
                }}
              />
            )}
            {canMove && (
              <FunctionField
                label='Move'
                render={(account: BaseAccount) => {
                  if (account.isRoot) {
                    return 'NA';
                  }

                  return <MoveAccountButton account={account} user={user} />;
                }}
              />
            )}
            <ShowButton label='Show' resource={DataProviderResource.Accounts} />
          </Datagrid>
        </ListContextProvider>
      </Grid>
    </Grid>
  );
};
